import * as React from "react"
import { X } from "lucide-react"
import { Alert, AlertTitle, AlertDescription } from "./alert"
import { cn } from "../../lib/utils"

/**
 * Bottom-right floating/toast-style alert (on the toast layer: above modal overlays). No toast library exists in
 * this app yet -- this reuses the existing `Alert` primitive (design
 * system's alert variant/typography) and only adds fixed positioning +
 * dismiss/auto-hide behavior, instead of inventing a new visual style.
 * If a real toast queue is ever added centrally, this can be replaced
 * without touching callers (same props shape).
 */
export interface FloatingAlertProps {
  title: string;
  description?: string;
  variant?: "default" | "destructive" | "warning";
  onDismiss?: () => void;
  /** An action under the message (e.g. a link to the record that was just created). */
  children?: React.ReactNode;
}

const variantClassName: Record<NonNullable<FloatingAlertProps["variant"]>, string> = {
  default: "",
  destructive: "",
  // Alert's cva only defines default/destructive -- warning reuses the same
  // amber tone already used elsewhere in the app (e.g. tagStatusPending) so
  // this doesn't introduce a new ad hoc color.
  warning: "border-yellow-400/60 text-yellow-800 [&>svg]:text-yellow-600 bg-yellow-50",
};

export function FloatingAlert({ title, description, variant = "default", onDismiss, children }: FloatingAlertProps) {
  return (
    <Alert
      variant={variant === "destructive" ? "destructive" : "default"}
      className={cn(
        "fixed bottom-6 right-6 z-[var(--z-toast)] w-[22rem] shadow-lg bg-white",
        variantClassName[variant]
      )}
    >
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <AlertTitle className="pr-6">{title}</AlertTitle>
      {description && <AlertDescription>{description}</AlertDescription>}
      {children && <div className="mt-2 text-sm font-medium">{children}</div>}
    </Alert>
  );
}
